"use client";

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Settings, Shield, Zap, Database, AlertCircle, Download, Upload } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { showSuccess, showError } from '@/utils/toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

const AdminSettings = () => {
  const { profile, company } = useAuth();
  const [settings, setSettings] = useState({
    enable_mfa: true,
    require_kyc: false,
    auto_backup: true,
    custom_domain: company?.custom_domain || '',
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!company?.id) throw new Error('No company');
      const { error } = await supabase
        .from('companies')
        .update(data)
        .eq('id', company.id);
      if (error) throw error;
    },
    onSuccess: () => {
      showSuccess('Settings updated!');
    },
    onError: (error: any) => {
      if (error.message.includes('permission')) {
        showError('Access denied. Contact superadmin.');
      } else {
        showError('Failed to update settings');
      }
    },
  });

  const backupMutation = useMutation({
    mutationFn: async () => {
      if (!company?.id) throw new Error('No company');
      const response = await fetch('/api/backup-company', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: company.id }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Backup failed');
      return result;
    },
    onSuccess: (result: any) => {
      showSuccess(`Backup created: ${result.backup_url}`);
    },
    onError: () => {
      showError('Failed to create backup');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(settings);
  };

  if (!company?.id || profile?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert>
          <AlertDescription>Access denied. Admin only.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Admin Settings</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Security Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="enable_mfa"
                checked={settings.enable_mfa}
                onCheckedChange={(checked) => setSettings({ ...settings, enable_mfa: checked })}
              />
              <Label htmlFor="enable_mfa">Require 2FA for all users</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="require_kyc"
                checked={settings.require_kyc}
                onCheckedChange={(checked) => setSettings({ ...settings, require_kyc: checked })}
              />
              <Label htmlFor="require_kyc">Require KYC verification for clients</Label>
            </div>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
              Update Security
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Backup & Export</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex space-x-2">
            <Button onClick={() => backupMutation.mutate()} disabled={backupMutation.isPending}>
              {backupMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Create Backup
            </Button>
            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" />
              Import Data
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            Automatic daily backups are enabled. Manual backups available above.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Advanced Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="custom_domain">Custom Domain</Label>
            <Input
              id="custom_domain"
              value={settings.custom_domain}
              onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })}
              placeholder="yourcompany.com"
            />
            <Button variant="outline" size="sm" onClick={() => {/* DNS verify */}}>
              Verify DNS
            </Button>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="auto_backup"
              checked={settings.auto_backup}
              onCheckedChange={(checked) => setSettings({ ...settings, auto_backup: checked })}
            />
            <Label htmlFor="auto_backup">Enable automatic daily backups</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="advanced_features"
              checked={true}
              onCheckedChange={() => {}}
              disabled
            />
            <Label htmlFor="advanced_features">Enable advanced AI features</Label>
            <Badge variant="secondary" className="ml-2">Coming Soon</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Compliance & Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Zap className="h-4 w-4 text-green-500" />
              <div>
                <div className="font-medium">2FA Enforcement</div>
                <div className="text-sm text-muted-foreground">Required for all users</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-blue-500" />
              <div>
                <div className="font-medium">Data Encryption</div>
                <div className="text-sm text-muted-foreground">At rest and in transit</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Database className="h-4 w-4 text-purple-500" />
              <div>
                <div className="font-medium">Audit Trail</div>
                <div className="text-sm text-muted-foreground">All actions logged</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              <div>
                <div className="font-medium">GDPR Compliance</div>
                <div className="text-sm text-muted-foreground">UK/EU data protection</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;